import ThreeContext from "./ThreeContext";
import Earth from "./earth/Earth";
import FlightInstances from "./flight/FlightInstances";
import FlightModel from "./flight/FlightModel";
import AirportModel from "./airport/AirportModel";
import Route from "./route/Route";
import { Raycaster, Vector2, Matrix4, Mesh, BoxBufferGeometry, Vector3 } from "three";
import { getUnix } from "@common/utils/Time";
import { increaseResolution, sphericalToCartesianThreeJs } from "./common/Coordinates";
import AirportInstances from "./airport/AirportInstances";
import { Settings, SettingsItem } from "@live/settings/Settings";
const ORIGINAL_CAMERA_DISTANCE = 2;
const ZOOM_DISCRETE_SCALE = 1.2;
const TARGET_R = 1.1;
export default class LiveScene extends ThreeContext {
    earth;
    flightModel;
    flights;
    airportModel;
    airports;
    route;
    fpl;
    isXRayOn;
    constructor(canvasSelector) {
        super(canvasSelector);
        this.setColour(0x0A0A14);
        const ξ = ORIGINAL_CAMERA_DISTANCE * Math.sqrt(3) / 3;
        this.setCameraPosition(ξ, ξ, ξ);
        this.earth = new Earth();
        this.add(this.earth.getObject3D());
        this.flightModel = new FlightModel();
        this.airportModel = new AirportModel();
        this.flights = null;
        this.airports = null;
        this.route = null;
        this.fpl = null;
        this.isXRayOn = false;
        this.startAnimationLoop((λCamera) => {
            this.airports?.update(λCamera);
            this.flights?.update(λCamera, this.isXRayOn);
            this.route?.update(λCamera);
            this.fpl?.update(λCamera);
            this.earth.update(this.isXRayOn);
        });
    }
    zoomIn() {
        const λ = this.getCameraPositionAsParam();
        this.setCameraPositionParam(λ / ZOOM_DISCRETE_SCALE);
    }
    zoomOut() {
        const λ = this.getCameraPositionAsParam();
        this.setCameraPositionParam(λ * ZOOM_DISCRETE_SCALE);
    }
    target(theta, phi) {
        const { x, y, z } = sphericalToCartesianThreeJs({ r: TARGET_R, theta, phi });
        this.setCameraPosition(x, y, z);
    }
    refreshRoutes(flightDetails) {
        const flown = flightDetails.route.map(({ altitude, theta, phi }) => { return { r: altitude, theta, phi }; });
        const fpl = flightDetails.fplCoordinates.map(({ theta, phi }) => { return { r: 1.0, theta, phi }; });
        const fplDetailed = increaseResolution(fpl, 200, true);
        this.p_refreshRoute(flown, 0x0197F6);
        this.p_refreshFpl(fplDetailed, 0xD90429);
    }
    p_refreshRoute(sphericalCoordinates, colour) {
        const str = Settings.get(SettingsItem.FLIGHT_ROUTE);
        const show = str == null || str != "false";
        if (!show) {
            return;
        }
        if (this.route != null) {
            this.remove(this.route.getObject3D());
            this.route.dispose();
            this.route = null;
        }
        this.route = new Route(sphericalCoordinates, colour, 1.001, 1.02);
        this.add(this.route.getObject3D());
    }
    p_refreshFpl(sphericalCoordinates, colour) {
        const str = Settings.get(SettingsItem.FLIGHT_ROUTE);
        const show = str == null || str != "false";
        if (!show) {
            return;
        }
        if (this.fpl != null) {
            this.remove(this.fpl.getObject3D());
            this.fpl.dispose();
            this.fpl = null;
        }
        this.fpl = new Route(sphericalCoordinates, colour, 1.001, 1.01);
        this.add(this.fpl.getObject3D());
    }
    async refreshFlights(flights) {
        const model = await this.flightModel.getModel();
        if (this.flights != null) {
            this.remove(this.flights.getObject3d());
            this.flights.dispose();
        }
        this.flights = new FlightInstances(model, getUnix(), flights);
        this.add(this.flights.getObject3d());
    }
    async refreshAirports(sphericalCoordinates) {
        const str = Settings.get(SettingsItem.DISPLAY_ACTIVE_AIRPORTS);
        const show = str == null || str != "false";
        if (!show) {
            return;
        }
        const model = await this.airportModel.getModel();
        if (this.airports != null) {
            this.remove(this.airports.getObject3d());
            this.airports.dispose();
        }
        this.airports = new AirportInstances(model, sphericalCoordinates);
        this.add(this.airports.getObject3d());
    }
    onClick(event, object) {
        if (object == null)
            return null;
        const mouse = new Vector2();
        const mouseRaycaster = new Raycaster();
        mouse.x = (event.offsetX / this.getClientWidth()) * 2 - 1;
        mouse.y = -(event.offsetY / this.getClientHeight()) * 2 + 1;
        mouseRaycaster.setFromCamera(mouse, this.getCamera());
        const intersects = this.intersectionInstancedMesh(mouseRaycaster, object.getObject3d());
        if (intersects.length <= 0)
            return null;
        const index = intersects[0].instanceId;
        return index == undefined ? null : index;
    }
    intersectionInstancedMesh(raycaster, instancedMesh) {
        const TARGET_SIZE_SCALAR = 1.25;
        const arr = [];
        const _mesh = new Mesh();
        const _instanceIntersects = [];
        const _instanceLocalMatrix = new Matrix4();
        const _instanceWorldMatrix = new Matrix4();
        const matrixWorld = instancedMesh.matrixWorld;
        const raycastTimes = instancedMesh.count;
        instancedMesh.geometry.computeBoundingBox();
        const boundingBox = instancedMesh.geometry.boundingBox;
        const size = new Vector3();
        boundingBox.getSize(size);
        size.multiplyScalar(TARGET_SIZE_SCALAR);
        _mesh.geometry = new BoxBufferGeometry(size.x, size.y, size.z);
        _mesh.material = instancedMesh.material;
        if (_mesh.material === undefined)
            return;
        for (let instanceId = 0; instanceId < raycastTimes; instanceId++) {
            instancedMesh.getMatrixAt(instanceId, _instanceLocalMatrix);
            _instanceWorldMatrix.multiplyMatrices(matrixWorld, _instanceLocalMatrix);
            _mesh.matrixWorld = _instanceWorldMatrix;
            _mesh.raycast(raycaster, _instanceIntersects);
            for (let i = 0, l = _instanceIntersects.length; i < l; i++) {
                const intersect = _instanceIntersects[i];
                intersect.instanceId = instanceId;
                intersect.object = instancedMesh;
                arr.push(intersect);
            }
            _instanceIntersects.length = 0;
        }
        return arr;
    }
    onClickFlights(event) {
        return this.onClick(event, this.flights);
    }
    onClickAirports(event) {
        return this.onClick(event, this.airports);
    }
    toggleXRay() {
        this.isXRayOn = !this.isXRayOn;
    }
    destroy() {
        if (this.flights != null) {
            this.remove(this.flights.getObject3d());
            this.flights.dispose();
        }
        if (this.airports != null) {
            this.remove(this.airports.getObject3d());
            this.airports.dispose();
        }
        if (this.route != null) {
            this.remove(this.route.getObject3D());
            this.route.dispose();
        }
        if (this.fpl != null) {
            this.remove(this.fpl.getObject3D());
            this.fpl.dispose();
        }
        this.remove(this.earth.getObject3D());
        this.earth.dispose();
        this.contextDestroy();
        this.stopAnimationLoop();
    }
}
